import React from 'react';
import Label from 'components/form/Label/Label';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { Button } from 'components/form/Button/Button';
import classnames from 'classnames';
import OText from 'components/CText/OText';
import { CreditCardAdditionalText } from 'components/Fragments/CreditCardAdditionalText/CreditCardAdditionalText';
import { ReactComponent as OpenInNew } from 'style/icons/open-in-new-blue-plus.svg';
import TextFromConfig from 'components/TextFromConfig';
import { CSTooltip } from 'components/Tooltip/CSToolTip';
import { formatIncome } from 'helpers/money.helper';
import { Image } from 'components/Image/Image';
import './StandardCreditCardPlus.scss';
import { CreditCardPreApprovedMessage } from 'components/Fragments/CreditCardPreApprovedMessage/CreditCardPreApprovedMessage';

const CreditCardTootTip = props => {
  const {
    offerMetadata,
    hideCreditLimitByConfig,
    aprovalOdds,
    accessUp,
    isCreditBuilder,
  } = props;
  const titleToolTip = accessUp ? (
    <TextFromConfig path={TextFromConfig?.paths?.DISCLAIMER_PREDICTED_LIMIT} />
  ) : (
    <TextFromConfig path={TextFromConfig?.paths?.DISCLAIMER_AO} />
  );

  const predictedCreditLimitLabel =
    formatIncome(offerMetadata?.predictedCreditLimit) ?? formatIncome(300);
  if (offerMetadata?.hideCreditLimit || hideCreditLimitByConfig) {
    return <>{isCreditBuilder || <FlexContainer></FlexContainer>}</>;
  } else {
    return (
      <FlexContainer>
        <CSTooltip
          title={titleToolTip}
          placement={'bottom'}
          isInline={true}
          tooltipName={'Credit Card Plus Tooltip'}
          pagePosition={props?.pagePosition}
        >
          <FlexContainer className={'items-align-center'}>
            {aprovalOdds && (
              <Label type={'captionBold fontWhite'}>Top approval odds</Label>
            )}
            {accessUp && (
              <Label type={'caption fontWhite'}>
                You may access up to: {predictedCreditLimitLabel}
              </Label>
            )}
            <Image imgClass={'questionMarkTooltip'} />
          </FlexContainer>
        </CSTooltip>
      </FlexContainer>
    );
  }
};

const OfferMetadataDetails = props => {
  const { offerMetadata, isCreditBuilder, clickApplyCardNameHandler } = props;
  return (
    <FlexContainer className={'flex-column gap-8'}>
      <FlexContainer className={'justify-between align-items-center'}>
        <Label
          type="Subtitle-2 fontWhite"
          className={`pointer`}
          onClick={e => {
            e?.stopPropagation();
            clickApplyCardNameHandler(e);
          }}
        >
          <OText text={offerMetadata?.name} />
        </Label>
        {isCreditBuilder && (
          <FlexContainer
            className={'minWidthImage items-align-center justify-center'}
          >
            <Image imgClass={'sesamePlus'} />
          </FlexContainer>
        )}
      </FlexContainer>
      <FlexContainer
        className={classnames('justify-between', {
          offerDetailsMaxWidth: isCreditBuilder,
        })}
      >
        <FlexContainer className={'flex-column gap-10'}>
          <Label type={'offerDetailsFont fontWhite'}>
            <span className={'bold'}>{'Annual Fee: '}</span>
            {isCreditBuilder && <br />}
            <span>{`${offerMetadata?.annualFeeDetails} `}</span>
          </Label>
          <Label type={'offerDetailsFont fontWhite'}>
            <span className={'bold'}>{`Regular APR: `}</span>
            {isCreditBuilder && <br />}
            <span>{`${offerMetadata?.aprDetails}`}</span>
          </Label>
        </FlexContainer>
        <FlexContainer
          className={classnames('flex-column items-align-center', {
            'justify-end minWidthImage': !isCreditBuilder,
          })}
        >
          {isCreditBuilder || (
            <Image imgClass={'sesamePick width120px height30px'} />
          )}
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  );
};

const ApplyNow = props => {
  const {
    isCreditBuilder,
    clickApplyHandler,
    buttonLabel,
    clickDetailsHandler,
    hideCreditLimitByConfig,
  } = props;
  return (
    <FlexContainer
      className={
        'flex-column gap-6 justify-center align-items-center m-width130px'
      }
    >
      {isCreditBuilder ? (
        <Label type={'caption fontWhite'}>You're Approved</Label>
      ) : (
        <CreditCardTootTip
          aprovalOdds
          hideCreditLimitByConfig={hideCreditLimitByConfig}
        />
      )}
      <Button
        mrphType={'yellowButton'}
        onClick={e => {
          e?.stopPropagation();
          clickApplyHandler(e);
        }}
        className={'width172px'}
        rounded
      >
        <Label
          className={classnames('justify-center align-items-center', {
            'm-text-12': buttonLabel?.length >= 20,
          })}
        >
          {buttonLabel}
        </Label>
      </Button>
      <Label
        type="Subtitle-3 fontWhite"
        className="otherLink"
        onClick={clickDetailsHandler}
      >
        {'View Details'}
      </Label>
    </FlexContainer>
  );
};

const StandardCreditCardPlus = props => {
  const {
    offerMetadata,
    offerImageLinkUrlProxy,
    extraAttributes,
    seenPixel,
    templateCustomClass,
    containerType,
    bodyContentType,
    clickApplyHandler,
    buttonLabel,
    clickDetailsHandler,
    hideCreditLimitByConfig,
    clickApplyCardNameHandler,
    clickTcHandler,
    clickApplyImageHandler,
    hideNAFields,
  } = props;

  const isCreditBuilder = extraAttributes?.isCreditBuilder;
  const cardClass = classnames(
    'CreditCardPlus flex-column bgNeutral450 borderRadius8 padB16 padL16 padR36 m-padR16 margR16 gap-10 m-margB16 m-marR0 m-marg0 offerCreditCardPlus',
    templateCustomClass,
    {
      padT24: isCreditBuilder,
      padT14: !isCreditBuilder,
    },
  );

  return (
    <>
      {seenPixel}
      <FlexContainer className={cardClass}>
        <CreditCardTootTip
          accessUp
          hideCreditLimitByConfig={hideCreditLimitByConfig}
          offerMetadata={offerMetadata}
          isCreditBuilder={isCreditBuilder}
        />
        <FlexContainer className={'justify-between fontWhite'}>
          <FlexContainer className={'flex-column fontWhite'}>
            <img
              alt="credit_card"
              onClick={e => {
                e?.stopPropagation();
                clickApplyImageHandler(e);
              }}
              src={offerImageLinkUrlProxy}
              className={
                'borderRadius3 borderwhite pointer width150px height94px'
              }
            />
          </FlexContainer>
          <ApplyNow
            isCreditBuilder={isCreditBuilder}
            clickApplyHandler={clickApplyHandler}
            buttonLabel={buttonLabel}
            clickDetailsHandler={clickDetailsHandler}
            hideCreditLimitByConfig={hideCreditLimitByConfig}
          />
        </FlexContainer>
        <OfferMetadataDetails
          offerMetadata={offerMetadata}
          isCreditBuilder={isCreditBuilder}
          clickApplyCardNameHandler={clickApplyCardNameHandler}
        />
        <CreditCardAdditionalText
          containerType={containerType}
          bodyContentType={bodyContentType}
          offerMetadata={offerMetadata}
          isPlus
          hideNAFields={hideNAFields}
        />
        {offerMetadata?.tcUrl && !isCreditBuilder && (
          <a
            className={'items-align-center pointer'}
            onClick={e => {
              e?.stopPropagation();
              clickTcHandler();
            }}
            href={offerMetadata?.tcUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Label type="Subtitle-3" className={'lBlue085'}>
              {offerMetadata?.tcText}
            </Label>
            <OpenInNew className="margL4" />
          </a>
        )}
        <CreditCardPreApprovedMessage
          className={'margt20 justify-center'}
          prePopulatedTooltipText={offerMetadata?.prePopulatedTooltipText}
          tooltipWhite
        />
      </FlexContainer>
    </>
  );
};

export default StandardCreditCardPlus;
